import React from 'react';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../hooks/useWebSocket';
import { ConnectionStatusPill } from './ConnectionStatusPill';

/**
 * Global, app-wide WebSocket health indicator.
 *
 * Mounts the singleton useWebSocket connection for the authenticated user and
 * displays <ConnectionStatusPill /> fixed in the bottom-right corner. Renders
 * nothing while logged out (the WS endpoint requires a user_id).
 *
 * NOTE: GroupChat.jsx also calls useWebSocket(user.user_id, ...) and the
 * underlying browser WebSocket is per-call, so right now we open two sockets
 * when GroupChat is mounted. The backend `services/websocket.manager` keeps a
 * single (user_id -> socket) map and replaces older sockets on reconnect, so
 * this is functionally safe. A future refactor can hoist useWebSocket into a
 * React context and have every consumer share one socket — out of scope for
 * this iteration's "small UX pill" ask.
 */
export const GlobalConnectionStatus = () => {
  const { isAuthenticated, user } = useAuth();

  if (!isAuthenticated || !user?.user_id) return null;

  return <GlobalConnectionStatusInner userId={user.user_id} />;
};

// Inner component so we only call the hook when we have a user_id (rules of hooks).
const GlobalConnectionStatusInner = ({ userId }) => {
  const { isConnected, reconnectAttempts, reconnect } = useWebSocket(userId);

  return (
    <ConnectionStatusPill
      isConnected={isConnected}
      reconnectAttempts={reconnectAttempts}
      onReconnect={reconnect}
      className="fixed bottom-4 right-4 z-50 shadow-lg shadow-black/40"
    />
  );
};

export default GlobalConnectionStatus;
